import { useCallback, useEffect, useMemo, useState } from "react";
import { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import { useNavigate } from "react-router-dom";

import { Popconfirm, Select, Space } from "antd/lib";
import { cloneDeep } from "lodash";
import './styles.css';
import { FormattedMessage, useIntl } from "react-intl";
import { SunkhronosCompany } from "../../../utils/types/applications/sunkhronosTypes";
import { FloatingSelectOption } from "../../common/Inputs/Floating/types";
import { SunkhronosCompanyTypes, SunkhronosCompanyVersions } from "../utils/constants";
import Network from "../../../utils/Network";
import { ConvertDateToNetworkDate, ConvertNetworkSunkhronosCompanies } from "../../../utils/converters";
import { getCompanyTypeLabel, getCompanyVersionLabel, showNotification } from "../../../utils/utils";
import FAIcon from "../../common/FAIcon/FAIcon";
import CircleButton from "../../common/CircleButton/CircleButton";
import VirtualTable from "../../common/VirtualTable/VirtualTable";
import getFormat from "../../../utils/lang";
import { useViewAddons } from "../../common/Layout/ViewAddons/ViewAddons";
import Row from "antd/es/row";
import Col from "antd/es/col";
import { SidebarEnum } from "../../common/Layout/ViewAddons/ViewAddonsRenderer";
import Fieldset from "../../common/Fieldset/Fieldset";
import FloatingInput from "../../common/Inputs/Floating/FloatingInput";
import Divider from "antd/es/divider";
import PopoverButton from "../../common/PopoverButton/PopoverButton";
import CreateCompanyDrawer from "../drawers/createCompany/CreateCompanyDrawer";
import EditCompanyDrawer from "../drawers/editCompany/EditCompanyDrawer";
import { Filter } from "../../common/Filter/Filter";
import Checkbox from "antd/es/checkbox/Checkbox";
import { DatePicker } from "antd";


const LOCAL_STORAGE_KEY_FILTERS = 'sunkhronosCompanyFilters';

const SunkhronosCompanies = () => {
    const intl = useIntl();
    const viewAddons = useViewAddons();

    const [createModal, setCreateModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [displayExpired, setDisplayExpired] = useState(false);
    const [companies, setCompanies] = useState<SunkhronosCompany[]>([]);

    //* Filters
    const updateFilterInLocalStorage = (key: string, value: any) => {
        try {
            const currentFilters = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY_FILTERS) || '{}');
            const updatedFilters = { ...currentFilters, [key]: value };
            localStorage.setItem(LOCAL_STORAGE_KEY_FILTERS, JSON.stringify(updatedFilters));
        } catch (err) {
            console.error('Failed to update localStorage filter:', err);
        }
    };

    const storedFilters = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY_FILTERS) || '{}');

    const [companyFilter, setCompanyFilter] = useState(storedFilters.companyFilter || '');
    const [companyTypeFilter, setCompanyTypeFilter] = useState<number[]>(storedFilters.companyTypeFilter || []);
    const [companyVersionFilter, setCompanyVersionFilter] = useState<number[]>(storedFilters.companyVersionFilter || []);
    const [selectedDate, setSelectedDate] = useState(dayjs(storedFilters.selectedDate || dayjs().startOf('month')));
    const [companyContractFilter, setCompanyContractFilter] = useState<number>(storedFilters.companyContractFilter || 1);
    const [selectedCompany, setSelectedCompany] = useState<SunkhronosCompany | undefined>(storedFilters.selectedCompany);

    const companyTypeOptions = useMemo((): FloatingSelectOption[] => SunkhronosCompanyTypes ? Object.values(SunkhronosCompanyTypes).map<FloatingSelectOption>(p => ({ label: p.label, value: p.value })) : [], []);
    const companyVersionOptions = useMemo((): FloatingSelectOption[] => SunkhronosCompanyVersions ? Object.values(SunkhronosCompanyVersions).map<FloatingSelectOption>(p => ({ label: p.label, value: p.value })) : [], []);
    const navigate = useNavigate();

    // #region Networks
    const handleFetchCompanies = useCallback(() => {
        setLoading(true);
        Network.fetchSunkhronosCompanies(ConvertDateToNetworkDate(selectedDate)).then(
            (res) => {
                setLoading(false);
                const tmpCompanies = ConvertNetworkSunkhronosCompanies(res.data);
                setCompanies(tmpCompanies);
            },
            () => {
                showNotification(intl.formatMessage({ defaultMessage: 'An error occurred while retrieving the companies' }), 'error');
                setLoading(false);
            }
        );
    }, [selectedDate, intl]);

    const handleRenewLicence = useCallback((id: number) => {
        setLoading(true);
        Network.renewSunkhronosLicence(id).then(
            (res) => {
                const tmpCompanies = cloneDeep(companies);
                const index = tmpCompanies.findIndex(c => c.id === res.data.id);
                if (index !== -1)
                    tmpCompanies[index] = {
                        ...tmpCompanies[index],
                        licenceFrom: res.data.licence_from ? dayjs(res.data.licence_from) : undefined,
                        licenceTo: res.data.licence_to ? dayjs(res.data.licence_to) : undefined,
                        lastInvoice: res.data.last_invoice ? dayjs(res.data.last_invoice) : undefined,
                    };

                setCompanies(tmpCompanies);
                setLoading(false);
            },
            () => {
                showNotification(intl.formatMessage({ defaultMessage: "An error occurred while updating the company's licence" }), 'error');
                setLoading(false);
            }
        );
    }, [companies, intl]);

    useEffect(() => {
        handleFetchCompanies();
    }, [handleFetchCompanies]);
    //#endregion

    //#region Warnings
    const licenceWarningDate = useMemo(() => dayjs().add(1, 'month').add(2, 'weeks'), []);
    const licenceErrorDate = useMemo(() => dayjs().add(1, 'month').add(-1, "day"), []);
    const contractWarningDate = useMemo(() => dayjs().add(4, 'month'), []);
    const contractErrorDate = useMemo(() => dayjs().add(3, 'month'), []);
    const terminationDelay = useMemo(() => { return 3; }, []);
    const contractResilationWarningDate = useMemo(() => dayjs().add(terminationDelay + 2, 'month'), [terminationDelay]);
    const contractResilationErrorDate = useMemo(() => dayjs().add(terminationDelay, 'month'), [terminationDelay]);
    //#endregion

    //#region Filtered companies
    const filteredCompanies = useMemo(() => {
        let tmpCompanies = cloneDeep(companies);

        if (companyFilter.length > 0)
            tmpCompanies = tmpCompanies.filter(c => c.name.toLocaleLowerCase().includes(companyFilter.toLocaleLowerCase()));

        if (companyTypeFilter.length > 0)
            tmpCompanies = tmpCompanies.filter(c => c.type !== undefined ? companyTypeFilter.includes(c.type) : false);

        if (companyVersionFilter.length > 0)
            tmpCompanies = tmpCompanies.filter(c => c.version !== undefined ? companyVersionFilter.includes(c.version) : false);

        if (companyContractFilter === 2)
            tmpCompanies = tmpCompanies.filter(c => c.hasContract === true);
        else if (companyContractFilter === 3)
            tmpCompanies = tmpCompanies.filter(c => c.hasContract === false);

        if (displayExpired)
            tmpCompanies = tmpCompanies.filter(c => c.expiration?.isBefore(dayjs().add(-1, 'days')));
        else
            tmpCompanies = tmpCompanies.filter(c => c.expiration?.isAfter(dayjs().add(-1, 'days')) || c.expiration === undefined);

        return tmpCompanies;
    }, [companies, companyContractFilter, companyFilter, companyTypeFilter, companyVersionFilter, displayExpired]);
    //#endregion

    //#region Status
    // Utilitaire local pour la classe de statut (error > warn)
    const getStatusClass = useCallback((date: dayjs.Dayjs | undefined, errorDate: dayjs.Dayjs, warningDate: dayjs.Dayjs) => {
        if (!date) return '';
        const today = dayjs();
        const oneYearAgo = today.add(-1, 'year');
        if (date.isBefore(oneYearAgo, 'date')) return '';
        if (date.isBefore(errorDate, 'date')) return ' error';
        if (date.isBefore(warningDate, 'date')) return ' warn';
        return '';
    }, []);

    // Pour la ligne : priorité error > warn sur licenceTo, contractFrom, contractTo
    const getRowStatusClass = useCallback((
        licenceTo: dayjs.Dayjs | undefined, licenceErrorDate: dayjs.Dayjs, licenceWarningDate: dayjs.Dayjs,
        contractTo: dayjs.Dayjs | undefined, contractErrorDate: dayjs.Dayjs, contractWarningDate: dayjs.Dayjs
    ) => {
        const isError = [
            getStatusClass(licenceTo, licenceErrorDate, licenceWarningDate),
            getStatusClass(contractTo, contractErrorDate, contractWarningDate)
        ].some(status => status === ' error');
        if (isError) return ' error';
        const isWarn = [
            getStatusClass(licenceTo, licenceErrorDate, licenceWarningDate),
            getStatusClass(contractTo, contractErrorDate, contractWarningDate)
        ].some(status => status === ' warn');
        if (isWarn) return ' warn';
        return '';
    }, [getStatusClass]);
    //#endregion

    //#region ViewAddons
    const updateViewAddonsSidebar = useCallback(() => {
        viewAddons.setSidebars([
            {
                button: {
                    icon: <FAIcon prefix='fad' name='filters' />,
                    title: intl.formatMessage({ defaultMessage: 'Filters' })
                },
                content:
                    <Row gutter={[10, 10]}>
                        <Col xs={{ span: 24 }} style={{ height: 'auto' }}>
                            <Fieldset legend={<><FAIcon prefix='fad' name='filters' /> <FormattedMessage defaultMessage={'Filters'} /></>}>
                                <Space direction='vertical' size={15}>

                                    <Filter title={<FormattedMessage defaultMessage={'Usage date'} />}
                                        input={
                                            <DatePicker
                                                style={{ width: '100%' }}
                                                picker='month'
                                                value={selectedDate}
                                                format={getFormat('MONTH_AND_YEAR')}
                                                allowClear={false}
                                                disabled={loading}
                                                onChange={(e) => {
                                                    const newDate = e.startOf('month');
                                                    setSelectedDate(newDate);
                                                    updateFilterInLocalStorage('selectedDate', newDate.toISOString());
                                                }}
                                            />
                                        }
                                    />
                                    <Filter title={<FormattedMessage defaultMessage={'Company type'} />}
                                        input={
                                            <Select
                                                style={{ width: '100%' }}
                                                value={companyTypeFilter}
                                                allowClear
                                                options={companyTypeOptions}
                                                mode='multiple'
                                                onChange={(e: number[]) => {
                                                    setCompanyTypeFilter(e);
                                                    updateFilterInLocalStorage('companyTypeFilter', e);
                                                }}
                                            />
                                        }
                                    />
                                    <Filter title={<FormattedMessage defaultMessage={'Company version'} />}
                                        input={
                                            <Select
                                                style={{ width: '100%' }}
                                                value={companyVersionFilter}
                                                allowClear
                                                options={companyVersionOptions}
                                                mode='multiple'
                                                onChange={(e: number[]) => {
                                                    setCompanyVersionFilter(e);
                                                    updateFilterInLocalStorage('companyVersionFilter', e);
                                                }}
                                            />
                                        }
                                    />
                                    <Filter title={<FormattedMessage defaultMessage={'Contract'} />}
                                        input={
                                            <Select
                                                style={{ width: '100%' }}
                                                value={companyContractFilter}
                                                onChange={(value: number) => {
                                                    setCompanyContractFilter(value);
                                                    updateFilterInLocalStorage('companyContractFilter', value);
                                                }}
                                                options={[
                                                    { value: 1, label: intl.formatMessage({ defaultMessage: 'All' }) },
                                                    { value: 2, label: intl.formatMessage({ defaultMessage: 'With the contract' }) },
                                                    { value: 3, label: intl.formatMessage({ defaultMessage: 'Without the contract' }) },
                                                ]}
                                            />
                                        }
                                    />
                                    <Filter title={<FormattedMessage defaultMessage={'Expired'} />}
                                        input={
                                            <Checkbox checked={displayExpired} onChange={(e) => setDisplayExpired(e.target.checked)}>
                                                <FormattedMessage defaultMessage={'Display expired companies only'} />
                                            </Checkbox>
                                        }
                                    />
                                </Space>
                            </Fieldset>
                        </Col>
                    </Row>,
                type: SidebarEnum.FILTERS
            }
        ]);
    }, [viewAddons, intl, selectedDate, loading, companyTypeFilter, companyTypeOptions, companyVersionFilter, companyVersionOptions, companyContractFilter, displayExpired]);

    const updateViewAddonsContent = useCallback(() => {
        viewAddons.setContent(
            <Space>
                <FloatingInput
                    label={intl.formatMessage({ defaultMessage: 'Company name' })}
                    value={companyFilter}
                    allowClear
                    onChange={(e) => {
                        setCompanyFilter(e.target.value);
                        updateFilterInLocalStorage('companyFilter', e.target.value);
                    }}
                />
                <CircleButton
                    onClick={handleFetchCompanies}
                    icon={<FAIcon prefix='fad' name='rotate' />}
                    loading={loading}
                />
            </Space>
        );
    }, [companyFilter, handleFetchCompanies, intl, loading, viewAddons]);

    const alerts = useMemo(() => {
        return filteredCompanies.map((record) => {
            const Errors: string[] = [];
            const Warnings: string[] = [];
            let isError = false;
            let isWarning = false;

            const today = dayjs();

            if (!record.expiration || dayjs(record.expiration).isAfter(today, 'date')) {
                // Vérification expiré (dans la limite d'un an)
                const oneYearAgo = dayjs().add(-1, 'year');
                if (record.licenceTo && record.licenceTo.isBefore(today, 'date') && record.licenceTo.isSameOrAfter(oneYearAgo, 'date')) {
                    Errors.push(intl.formatMessage(
                        { defaultMessage: "The licence of the company {company} has expired" },
                        { company: record.name }
                    ));
                    isError = true;
                }
                if (record.contractTo && record.contractTo.isBefore(today, 'date') && record.contractTo.isAfter(oneYearAgo, 'date')) {
                    Errors.push(intl.formatMessage(
                        { defaultMessage: "The contract of the company {company} has expired" },
                        { company: record.name }
                    ));
                    isError = true;
                }
                if (record.licenceTo && record.licenceTo.isBefore(licenceErrorDate, 'date') && record.licenceTo.isAfter(today, 'date')) {
                    Errors.push(intl.formatMessage(
                        { defaultMessage: "The licence of the company {company} expires soon" },
                        { company: record.name }
                    ));
                    isError = true;
                }
                if (record.contractTo && record.contractTo.isBefore(contractErrorDate, 'date') && record.contractTo.isAfter(today, 'date')) {
                    Errors.push(intl.formatMessage(
                        { defaultMessage: "The contract of the company {company} expires soon" },
                        { company: record.name }
                    ));
                    isError = true;
                }
                if (record.licenceTo && record.licenceTo.isBefore(licenceWarningDate, 'date') && record.licenceTo.isAfter(today, 'date')) {
                    Warnings.push(intl.formatMessage(
                        { defaultMessage: "The licence of the company {company} will expire soon" },
                        { company: record.name }
                    ));
                    isWarning = true;
                }
                if (record.contractTo && record.contractTo.isBefore(contractWarningDate, 'date') && record.contractTo.isAfter(today, 'date')) {
                    Warnings.push(intl.formatMessage(
                        { defaultMessage: "The contract of the company {company} will expire soon" },
                        { company: record.name }
                    ));
                    isWarning = true;
                }
            }

            return {
                ...record,
                Errors,
                Warnings,
                isError,
                isWarning,
            };
        });
    }, [contractErrorDate, contractWarningDate, filteredCompanies, intl, licenceErrorDate, licenceWarningDate]);

    const alertsError = useMemo(() => {
        return alerts.flatMap((rec, recIndex) =>
            rec.Errors.map((error, index) => (
                <li key={`error-${recIndex}-${index}`}>{error}</li>
            ))
        );
    }, [alerts]);

    const alertsWarnings = useMemo(() => {
        return alerts.flatMap((rec, recIndex) =>
            rec.Warnings.map((warn, index) => (
                <li key={`warning-${recIndex}-${index}`}>{warn}</li>
            ))
        );
    }, [alerts]);

    const updateViewAddonsExtra = useCallback(() => {

        const hasErrors = alerts.some(rec => rec.Errors.length > 0);
        const hasWarnings = alerts.some(rec => rec.Warnings.length > 0);

        viewAddons.setExtra(
            [
                <PopoverButton
                    content={
                        <div>
                            {hasErrors && (
                                <>
                                    <span><FormattedMessage defaultMessage="Errors :" /></span>
                                    <ul>
                                        {alertsError}
                                    </ul>
                                </>
                            )}

                            {hasWarnings && (
                                <>
                                    <span><FormattedMessage defaultMessage="Warnings :" /></span>
                                    <ul>
                                        {alertsWarnings}
                                    </ul>
                                </>
                            )}

                            {!hasErrors && !hasWarnings && <p><FormattedMessage defaultMessage={'No Errors'} /></p>}
                        </div>
                    }
                    prefix="fad"
                    nameicone="triangle-exclamation"
                    beat={hasErrors || hasWarnings}
                    danger={hasErrors || hasWarnings}
                />,
                <PopoverButton
                    title={intl.formatMessage({ defaultMessage: "Help" })}
                    content={
                        <div>
                            <><FormattedMessage defaultMessage="License:" /></>
                            <ul>
                                <li>
                                    <FormattedMessage
                                        defaultMessage="License in 1 month and 2 weeks → The entire line turns {color}."
                                        values={{ color: <span style={{ color: 'var(--yellow)' }}>{<FormattedMessage defaultMessage="yellow" />}</span> }}
                                    />
                                </li>
                                <li>
                                    <FormattedMessage
                                        defaultMessage="License in less than a month → The entire line turns {color}."
                                        values={{ color: <span style={{ color: 'var(--red)' }}>{<FormattedMessage defaultMessage="red" />}</span> }}
                                    />
                                </li>
                                <li>
                                    <FormattedMessage
                                        defaultMessage="Expired license → The text and the line are {color}."
                                        values={{ color: <span style={{ color: 'var(--red)' }}>{<FormattedMessage defaultMessage="red" />}</span> }}
                                    />
                                </li>
                            </ul>
                            <><FormattedMessage defaultMessage="Contract:" /></>
                            <ul>
                                <li>
                                    <FormattedMessage
                                        defaultMessage="Contract in less than 4 months → The entire line turns {color}."
                                        values={{ color: <span style={{ color: 'var(--yellow)' }}>yellow</span> }}
                                    />
                                </li>
                                <li>
                                    <FormattedMessage
                                        defaultMessage="Contract in less than 3 months → The entire line turns {color}."
                                        values={{ color: <span style={{ color: 'var(--red)' }}>{<FormattedMessage defaultMessage="red" />}</span> }}
                                    />
                                </li>
                            </ul>
                            <><FormattedMessage id="section.termination" defaultMessage="Termination:" /></>
                            <ul>
                                <li>
                                    <FormattedMessage
                                        defaultMessage="Termination before 5 months → Only the relevant column turns {color}."
                                        values={{ color: <span style={{ color: 'var(--yellow)' }}>{<FormattedMessage defaultMessage="yellow" />}</span> }}
                                    />
                                </li>
                                <li>
                                    <FormattedMessage
                                        defaultMessage="Termination before 3 months → Only the relevant column turns {color}."
                                        values={{ color: <span style={{ color: 'var(--red)' }}>{<FormattedMessage defaultMessage="red" />}</span> }}
                                    />
                                </li>
                            </ul>
                        </div>
                    }
                />,
                <Divider type='vertical' dashed />,
                <CircleButton
                    onClick={() => setCreateModal(true)}
                    icon={<FAIcon prefix='far' name='plus' />}
                />
            ]
        );
    }, [alerts, alertsError, alertsWarnings, intl, viewAddons]);

    useEffect(() => {
        updateViewAddonsSidebar();
        updateViewAddonsContent();
        updateViewAddonsExtra();
    });
    //#endregion

    const calculateTermination = useCallback((date: Dayjs, delay: number = 3) => {
        return date.add(-delay, 'month');
    }, []);

    const seeContract = useCallback((company: SunkhronosCompany) => {
        const companyId = company.id;
        if (!companyId) return;

        Network.fetchContract(companyId).then(
            (blob) => {
                window.open(URL.createObjectURL(blob.data));
            },
            () => {
                showNotification(intl.formatMessage({ defaultMessage: "An error occurred while opening the company's contract" }), 'error');
            }
        );
    }, [intl]);

    const seeOffer = useCallback((company: SunkhronosCompany) => {
        const companyId = company.id;
        if (!companyId) return;

        Network.fetchOffer(companyId).then(
            (blob) => {
                window.open(URL.createObjectURL(blob.data));
            },
            () => {
                showNotification(intl.formatMessage({ defaultMessage: "An error occurred while opening the company's offer" }), 'error');
            }
        );
    }, [intl]);

    //*Columns
    const columns = useMemo((): ColumnsType<SunkhronosCompany> => [
        {
            key: 'name',
            dataIndex: 'name',
            title: intl.formatMessage({ defaultMessage: 'Company name' }),
            width: 300,
            onFilter: (value, record) => {
                return record.name
                    .toString()
                    .toLowerCase()
                    .includes(value.toString().toLowerCase());
            },
            sorter: (a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1),
        },
        {
            key: 'expiration',
            dataIndex: 'expiration',
            title: intl.formatMessage({ defaultMessage: 'Expiration' }),
            align: 'center',
            width: 120,
            render: (_, record) => record.expiration ? record.expiration.format(getFormat('DATE')) : '-',
            sorter: (a, b) => {
                if (!a.expiration && !b.expiration) return 0; // Both are undefined or falsy
                if (!a.expiration) return 1; // Treat undefined as greater than a defined date
                if (!b.expiration) return -1; // Treat undefined as less than a defined date
                return dayjs(a.expiration).isBefore(dayjs(b.expiration)) ? -1 : 1; // Compare valid dates
            },
        },

        //TODO: INVOICE MODULE NOT READY
        {
            key: 'licence',
            title: intl.formatMessage({ defaultMessage: 'Licence' }),
            className: 'odd',
            width: 240,
            children: [
                {
                    key: 'licence_from',
                    dataIndex: 'licence_from',
                    title: intl.formatMessage({ defaultMessage: 'From' }),
                    align: 'center',
                    className: 'odd',
                    width: 120,
                    render: (_, record) => record.licenceFrom ? record.licenceFrom.format(getFormat('DATE')) : '-',
                    sorter: (a, b) => {
                        if (!a.licenceFrom && !b.licenceFrom) return 0; // Both are undefined or falsy
                        if (!a.licenceFrom) return 1; // Treat undefined as greater than a defined date
                        if (!b.licenceFrom) return -1; // Treat undefined as less than a defined date
                        return dayjs(a.licenceFrom).isBefore(dayjs(b.licenceFrom)) ? -1 : 1; // Compare valid dates
                    },
                },
                {
                    key: 'licence_to',
                    dataIndex: 'licence_to',
                    title: intl.formatMessage({ defaultMessage: 'To' }),
                    align: 'center',
                    className: 'odd',
                    width: 120,
                    render: (_, record) => (
                        <span className={`licence${getStatusClass(record.licenceTo, licenceErrorDate, licenceWarningDate)}`}>
                            {record.licenceTo?.format(getFormat('DATE')) || '-'}
                        </span>
                    ),
                    sorter: (a, b) => {
                        if (!a.licenceTo && !b.licenceTo) return 0; // Both are undefined or falsy
                        if (!a.licenceTo) return 1; // Treat undefined as greater than a defined date
                        if (!b.licenceTo) return -1; // Treat undefined as less than a defined date
                        return dayjs(a.licenceTo).isBefore(dayjs(b.licenceTo)) ? -1 : 1; // Compare valid dates
                    },
                },
            ]
        },
        {
            key: 'last_invoice',
            dataIndex: 'last_invoice',
            title: intl.formatMessage({ defaultMessage: 'Last invoice' }),
            align: 'center',
            className: 'even',
            width: 150,
            render: (_, record) => record.lastInvoice ? record.lastInvoice.format(getFormat('DATE')) : '-',
            sorter: (a, b) => {
                if (!a.lastInvoice && !b.lastInvoice) return 0; // Both are undefined or falsy
                if (!a.lastInvoice) return 1; // Treat undefined as greater than a defined date
                if (!b.lastInvoice) return -1; // Treat undefined as less than a defined date
                return dayjs(a.lastInvoice).isBefore(dayjs(b.lastInvoice)) ? -1 : 1; // Compare valid dates
            },
        },
        {
            key: 'contract',
            title: intl.formatMessage({ defaultMessage: 'Contract' }),
            className: 'odd',
            width: 360,
            children: [
                {
                    key: 'contract_terminated',
                    dataIndex: 'contract_terminated',
                    title: intl.formatMessage({ defaultMessage: 'Status' }),
                    align: 'center',
                    className: 'odd',
                    width: 100,
                    render: (_, record) => record.contractTerminated ? <FAIcon prefix='fad' name='ban' /> : <FAIcon prefix='fad' name='circle-check' color="green" />,
                    sorter: (a, b) => {
                        if (!a.contractTerminated && !b.contractTerminated) return 0; // Both are undefined or falsy
                        if (!a.contractTerminated) return 1; // Treat undefined as greater than a defined date
                        if (!b.contractTerminated) return -1; // Treat undefined as less than a defined date
                        return a.contractTerminated === b.contractTerminated ? 0 : a.contractTerminated ? 1 : -1; // Compare valid dates
                    },
                },
                {
                    key: 'contract_from',
                    dataIndex: 'contract_from',
                    title: intl.formatMessage({ defaultMessage: 'From' }),
                    align: 'center',
                    className: 'odd',
                    width: 120,
                    render: (_, record) => record.contractFrom ? record.contractFrom.format(getFormat('DATE')) : '-',
                    sorter: (a, b) => {
                        if (!a.contractFrom && !b.contractFrom) return 0; // Both are undefined or falsy
                        if (!a.contractFrom) return 1; // Treat undefined as greater than a defined date
                        if (!b.contractFrom) return -1; // Treat undefined as less than a defined date
                        return dayjs(a.contractFrom).isBefore(dayjs(b.contractFrom)) ? -1 : 1; // Compare valid dates
                    },
                },
                {
                    key: 'contract_to',
                    dataIndex: 'contract_to',
                    title: intl.formatMessage({ defaultMessage: 'To' }),
                    align: 'center',
                    className: 'odd',
                    width: 120,
                    render: (_, record) => (
                        <span className={`contract${getStatusClass(record.contractTo, contractErrorDate, contractWarningDate)}`}>
                            {record.contractTo?.format(getFormat('DATE')) || '-'}
                        </span>
                    ),
                    sorter: (a, b) => {
                        if (!a.contractTo && !b.contractTo) return 0; // Both are undefined or falsy
                        if (!a.contractTo) return 1; // Treat undefined as greater than a defined date
                        if (!b.contractTo) return -1; // Treat undefined as less than a defined date
                        return dayjs(a.contractTo).isBefore(dayjs(b.contractTo)) ? -1 : 1; // Compare valid dates
                    },
                },
                {
                    key: 'termination',
                    dataIndex: 'termination',
                    title: intl.formatMessage({ defaultMessage: 'Termination' }),
                    align: 'center',
                    className: 'odd',
                    width: 120,
                    render: (_, record) => {
                        const termination = record.contractTo ? calculateTermination(record.contractTo, terminationDelay) : undefined;
                        return (
                            <span className={`contract${getStatusClass(termination, contractResilationErrorDate, contractResilationWarningDate)}`}>
                                {termination?.format(getFormat('DATE')) || '-'}
                            </span>
                        );
                    },
                    sorter: (a, b) => {
                        const terminationDelay = 3;
                        const aResilation = a.contractTo ? calculateTermination(a.contractTo, terminationDelay) : undefined;
                        const bResilation = b.contractTo ? calculateTermination(b.contractTo, terminationDelay) : undefined;

                        if (!bResilation && !aResilation) return 0; // Both are undefined or falsy
                        if (!aResilation) return 1; // Treat undefined as greater than a defined date
                        if (!bResilation) return -1; // Treat undefined as less than a defined date
                        return dayjs(aResilation).isBefore(dayjs(bResilation)) ? -1 : 1; // Compare valid dates
                    },
                },
                {
                    key: 'trial_period',
                    dataIndex: 'contract_trial_period',
                    title: intl.formatMessage({ defaultMessage: 'Trial period' }),
                    align: 'center',
                    className: 'odd',
                    width: 120,
                    render: (_, record) => record.contractTrialPeriod ? record.contractTrialPeriod.format(getFormat('DATE')) : '-',
                    sorter: (a, b) => {
                        if (!a.contractTrialPeriod && !b.contractTrialPeriod) return 0; // Both are undefined or falsy
                        if (!a.contractTrialPeriod) return 1; // Treat undefined as greater than a defined date
                        if (!b.contractTrialPeriod) return -1; // Treat undefined as less than a defined date
                        return dayjs(a.contractFrom).isBefore(dayjs(b.contractFrom)) ? -1 : 1; // Compare valid dates
                    },
                },
            ]
        },
        {
            key: 'offer_signed',
            dataIndex: 'offer_signed',
            title: intl.formatMessage({ defaultMessage: 'Offer signed' }),
            align: 'center',
            className: 'even',
            width: 150,
            render: (_, record) => record.offerSigned
                ? <FAIcon prefix='fad' name='circle-check' color="green" />
                : <FAIcon prefix='fad' name='circle-xmark' color="red" />,
            sorter: (a, b) => {
                if (a.offerSigned === b.offerSigned) return 0;
                return a.offerSigned ? -1 : 1;
            },
        },
        //TODO: INVOICE MODULE NOT READY

        {
            key: 'events',
            dataIndex: 'events',
            title: intl.formatMessage({ defaultMessage: 'Events' }),
            className: 'even',
            align: 'center',
            width: 130,
            render: (_, record) => record.events,
            sorter: (a, b) => {
                if (a.events === undefined && b.events === undefined) return 0; // Both are undefined or falsy
                if (a.events === undefined) return 1; // Treat undefined as greater than a defined date
                if (b.events === undefined) return -1; // Treat undefined as less than a defined date
                return a.events < b.events ? -1 : 1; // Compare valid dates
            },
        },
        {
            key: 'type',
            dataIndex: 'type',
            className: 'odd',
            title: intl.formatMessage({ defaultMessage: 'Type' }),
            width: 120,
            render: (_, record) => record.type !== undefined ? getCompanyTypeLabel(record.type) : 'Unknown',
            sorter: (a, b) => {
                if (a.type === undefined && b.type === undefined) return 0;
                if (a.type === undefined) return 1;
                if (b.type === undefined) return -1;
                return a.type - b.type;
            },
        },
        {
            key: 'version',
            dataIndex: 'version',
            title: intl.formatMessage({ defaultMessage: 'Version' }),
            className: 'even',
            width: 150,
            render: (_, record) => getCompanyVersionLabel(record.version),
            sorter: (a, b) => {
                if (a.version == null && b.version == null) return 0;
                if (a.version == null) return 1;  // undefined is treated as larger, so it comes later
                if (b.version == null) return -1; // undefined is treated as larger, so it comes later
                return a.version - b.version;
            }
        },
        {
            key: 'comment',
            className: 'odd',
            dataIndex: 'comment',
            title: intl.formatMessage({ defaultMessage: 'Comment' }),
            width: 250,
            render: (_, record) => record.comment,
        },
        {
            key: 'action',
            dataIndex: 'action',
            title: '',
            width: 180,
            fixed: 'right',
            render: (_, record) => (
                <Space align='center'>
                    <CircleButton
                        icon={<FAIcon prefix="fad" name="file-invoice" />}
                        title={intl.formatMessage({ defaultMessage: 'See the offer' })}
                        onClick={(e) => {
                            e.stopPropagation();
                            seeOffer(record);
                        }}
                        disabled={!record.hasOffer}
                    />
                    <CircleButton
                        icon={<FAIcon prefix="fad" name="file-contract" />}
                        title={intl.formatMessage({ defaultMessage: 'See the contract' })}
                        onClick={(e) => {
                            e.stopPropagation();
                            seeContract(record);
                        }}
                        disabled={!record.hasContract}
                    />
                    <Popconfirm
                        title={intl.formatMessage({ defaultMessage: 'Renewing licence' })}
                        description={
                            <>
                                <p>{intl.formatMessage({ defaultMessage: "This action will renew this company's licence" })}</p>
                                <p>{intl.formatMessage({ defaultMessage: "Do you want to continue?" })}</p>
                            </>
                        }
                        icon={<FAIcon prefix='fad' name='light-emergency-on' color='red' />}
                        onConfirm={(e) => {
                            if (e) {
                                e.preventDefault();
                                e.stopPropagation();
                            }
                            handleRenewLicence(record.id);
                        }}
                        placement='left'
                        onCancel={(e) => {
                            e?.preventDefault();
                            e?.stopPropagation();
                        }}
                    >
                        <CircleButton
                            icon={<FAIcon prefix='fad' name='repeat' />}
                            title={intl.formatMessage({ defaultMessage: 'Renew licence' })}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                            }}
                        />
                    </Popconfirm>
                    <CircleButton
                        icon={<FAIcon prefix='fad' name='pencil' />}
                        title={intl.formatMessage({ defaultMessage: 'Edit company' })}
                        onClick={(e) => {
                            e.stopPropagation();
                            setSelectedCompany(cloneDeep(record));
                        }}
                    />
                </Space>
            ),
        },
    ], [calculateTermination, contractErrorDate, contractResilationErrorDate, contractResilationWarningDate, contractWarningDate, getStatusClass, handleRenewLicence, intl, licenceErrorDate, licenceWarningDate, seeContract, seeOffer, terminationDelay]);

    return (
        <div>
            <VirtualTable<SunkhronosCompany>
                loading={{
                    spinning: loading,
                    indicator: <FAIcon prefix='fad' name='spinner-third' style={{ width: 50, height: 50 }} spin />
                }}
                virtual
                dataSource={filteredCompanies}
                columns={columns}
                pagination={false}
                height={190}
                rowKey={(record) => `sunk-company-${record.id}`}
                onRow={(record) => {
                    const className = 'company' + getRowStatusClass(
                        record.licenceTo, licenceErrorDate, licenceWarningDate,
                        record.contractTo, contractErrorDate, contractWarningDate
                    );
                    return {
                        onClick: () => {
                            navigate(`${record.id}/usage`, { state: record.name });
                        },
                        className,
                    };
                }}
            />
            <EditCompanyDrawer
                open={selectedCompany !== undefined}
                onCancel={(company) => {
                    if (company !== undefined) {
                        const tmpCompanies = cloneDeep(companies);
                        const index = tmpCompanies.findIndex(c => c.id === company.id);
                        if (index !== -1)
                            tmpCompanies[index] = company;

                        setCompanies(tmpCompanies);
                    }

                    setSelectedCompany(undefined);
                }}
                selectedCompany={selectedCompany}
                selectedDate={selectedDate}
            />
            <CreateCompanyDrawer open={createModal !== false} onCancel={(company) => {
                if (company !== undefined) {
                    const tmpCompanies = cloneDeep(companies);
                    tmpCompanies.push(company);
                    setCompanies(tmpCompanies);
                }
                setCreateModal(false);
            }} />
        </div>
    );
};

export default SunkhronosCompanies;